import { useState } from "react"
import { Typography, Row, Col, InputNumber, Button, Input } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { numberToCurrency } from "helpers/string"
import { EditOutlined, ReloadOutlined, CheckOutlined } from "@ant-design/icons"
import styles from "./BookingForm.module.css"

const { Text } = Typography

// New Booking's Subtotal row with its inline Adjust: the guide types a new subtotal (and an optional
// reason) into the form's adjustedPrice / adjustmentReason, and the price is recalculated from it.
// Shared by a private booking's PriceBreakdown and the Order Summary of an order or a ticketed
// booking. `isEditable` false shows a carried-over adjustment read-only (edit mode).
// `addOnsTotal` is the add-ons a shown subtotal includes (QA Ticketing #43). Adjust still edits the
// guests' price alone — adjustedPrice is the API's adjusted_subtotal, the trip price — so it opens on
// the subtotal less the add-ons, and the add-ons are added on top of what the guide types.
const SubtotalAdjust = ({
  form,
  originalSubtotal,
  subtotal,
  isAdjusted = false,
  adjustmentReason = null,
  isEditable = true,
  addOnsTotal = 0
}) => {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)
  const [localValue, setLocalValue] = useState(null)
  const [localReason, setLocalReason] = useState("")

  const handleEditClick = () => {
    setLocalValue(isAdjusted ? form.getFieldValue("adjustedPrice") : originalSubtotal - addOnsTotal)
    setLocalReason(adjustmentReason ?? "")
    setIsEditing(true)
  }

  const handleApply = () => {
    form.setFieldsValue({ adjustedPrice: localValue, adjustmentReason: localReason?.trim() || null })
    setIsEditing(false)
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  const handleReset = () => {
    form.setFieldsValue({ adjustedPrice: null, adjustmentReason: null })
    setLocalReason("")
    setIsEditing(false)
  }

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginTop: 8 }}>
        <Col>
          <Text style={{ fontWeight: 500 }}>{t("booking.labels.subtotal", "Subtotal")}</Text>
          {isEditable && !isEditing && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={handleEditClick}
              style={{ padding: "0 4px", color: "var(--color-primary)", fontSize: 13 }}
            >
              {t("global.adjust", "Adjust")}
            </Button>
          )}
          {isEditable && !isEditing && isAdjusted && (
            <Button
              type="link"
              size="small"
              icon={<ReloadOutlined />}
              onClick={handleReset}
              style={{ padding: "0 4px", color: "var(--color-text-secondary)", fontSize: 13 }}
            >
              {t("booking.labels.reset_adjustment")}
            </Button>
          )}
        </Col>
        <Col>
          {isAdjusted ? (
            <span>
              <Text delete type="secondary" style={{ marginRight: 6 }}>
                {numberToCurrency(originalSubtotal)}
              </Text>
              <Text strong style={{ color: "var(--color-primary)", marginRight: 4 }}>
                {numberToCurrency(subtotal)}
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                ({t("global.adjusted", "adjusted")})
              </Text>
            </span>
          ) : (
            <Text>{numberToCurrency(originalSubtotal)}</Text>
          )}
        </Col>
      </Row>

      {/* Reason is shown in edit mode too (read-only) so the carried-over adjustment stays legible. */}
      {!isEditing && isAdjusted && adjustmentReason && (
        <Row style={{ marginTop: 2 }}>
          <Col>
            <Text type="secondary" style={{ fontSize: 12, fontStyle: "italic" }}>
              {t("booking.labels.reason", "Reason")}: {adjustmentReason}
            </Text>
          </Col>
        </Row>
      )}

      {isEditable && isEditing && (
        <div className={styles.adjustPricePanel}>
          <Text className={styles.adjustPricePanelTitle}>
            {addOnsTotal > 0 ? t("booking.adjusted_guests_price_label") : t("booking.adjusted_price_label", "Adjusted Price (Subtotal)")}
          </Text>
          <InputNumber
            value={localValue}
            onChange={setLocalValue}
            min={0}
            precision={2}
            prefix="$"
            size="large"
            style={{ width: "100%", marginTop: 8 }}
            autoFocus
            // Numeric-only: block letters/"e"/symbols on keypress and strip them on paste.
            onKeyDown={(e) => {
              const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End", "Enter"]
              if (allowedKeys.includes(e.key) || e.metaKey || e.ctrlKey) return
              if (!/^[0-9.]$/.test(e.key)) e.preventDefault()
            }}
            parser={(value) => (value ? value.replace(/[^\d.]/g, "") : "")}
          />
          <Input.TextArea
            value={localReason}
            onChange={(e) => setLocalReason(e.target.value)}
            placeholder={t("booking.adjustment_reason_placeholder", "Reason for adjustment (optional)")}
            autoSize={{ minRows: 2, maxRows: 4 }}
            maxLength={255}
            style={{ marginTop: 8 }}
          />
          <Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: "block" }}>
            {t("booking.adjust_price_hint", "Fees are automatically recalculated based on adjusted price")}
          </Text>
          {addOnsTotal > 0 && (
            <Text type="secondary" style={{ fontSize: 12, marginTop: 2, display: "block" }}>
              {t("booking.adjust_price_add_ons_hint", { amount: numberToCurrency(addOnsTotal) })}
            </Text>
          )}
          <Row justify="end" gutter={8} style={{ marginTop: 12 }}>
            <Col flex="none">
              <Button size="large" onClick={handleCancel}>
                {t("global.cancel")}
              </Button>
            </Col>
            <Col flex="none">
              <Button type="primary" icon={<CheckOutlined />} size="large" onClick={handleApply} disabled={localValue == null}>
                {t("global.apply", "Apply")}
              </Button>
            </Col>
          </Row>
        </div>
      )}
    </>
  )
}

SubtotalAdjust.propTypes = {
  form: PropTypes.object.isRequired,
  originalSubtotal: PropTypes.number.isRequired,
  subtotal: PropTypes.number.isRequired,
  isAdjusted: PropTypes.bool,
  adjustmentReason: PropTypes.string,
  isEditable: PropTypes.bool,
  addOnsTotal: PropTypes.number
}

export default SubtotalAdjust
